// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { put, call, takeLatest, takeEvery } from 'redux-saga/effects';
import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
} from './../../../store/actionStatus';

import UserInfoDal from './../../../dal/UserInfoDal';
import {
    GET_ALL_USERS,
    GET_CHANNELS,
    GET_ROLES,
    GET_SOLD_TO_SHIP_TO,
    GET_TIERS,
    POST_USER,
    DELETE_USER,
    getAllUsersSuccess,
    getChannelsSuccess,
    getRolesSuccess,
    getSoldToShipToSuccess,
    getTiersSuccess,
    postUserSuccess,
    deleteUserSuccess,
    CLEAR_USER_MESSAGE,
    clearTiers,
} from './../../../store/manageUsers';

export function* getAllUsers(): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: GET_ALL_USERS }));
        const response = yield call(UserInfoDal.getAllUsers);
        yield put(getAllUsersSuccess(response));
        yield put(actionStatusSuccess({ actionName: GET_ALL_USERS }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_ALL_USERS,
                error: e,
            })
        );
    }
}

export function* getAllSoldToShipTo(): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: GET_SOLD_TO_SHIP_TO }));
        const response = yield call(UserInfoDal.getSoldToShipTo);
        yield put(getSoldToShipToSuccess(response));
        yield put(actionStatusSuccess({ actionName: GET_SOLD_TO_SHIP_TO }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_SOLD_TO_SHIP_TO,
                error: e,
            })
        );
    }
}

export function* getAllRoles(): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: GET_ROLES }));
        const response = yield call(UserInfoDal.getRoles);
        yield put(getRolesSuccess(response));
        yield put(actionStatusSuccess({ actionName: GET_ROLES }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_ROLES,
                error: e,
            })
        );
    }
}

export function* getAllChannels(): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: GET_CHANNELS }));
        const response = yield call(UserInfoDal.getChannels);
        yield put(getChannelsSuccess(response));
        yield put(actionStatusSuccess({ actionName: GET_CHANNELS }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_CHANNELS,
                error: e,
            })
        );
    }
}

export function* getFilteredTiers(request: any): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: GET_TIERS }));

        const response = yield call(UserInfoDal.getTiers, request);
        yield put(getTiersSuccess(response));
        yield put(actionStatusSuccess({ actionName: GET_TIERS }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: GET_TIERS,
                error: e,
            })
        );
        yield put(clearTiers());
    }
}

export function* postUser(request: any): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: POST_USER }));
        const response = yield call(UserInfoDal.createUser, request);
        yield put(postUserSuccess(response));
        yield put(actionStatusSuccess({ actionName: POST_USER }));
        return response;
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: POST_USER,
                error: e,
            })
        );
    }
}

export function* deleteUser(request: any): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: DELETE_USER }));
        const response = yield call(UserInfoDal.deleteUser, request);
        yield put(deleteUserSuccess(response));
        yield put(actionStatusSuccess({ actionName: DELETE_USER }));
        return response;
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: DELETE_USER,
                error: e,
            })
        );
    }
}

function* clearMessage(): IterableIterator<any> {
    try {
        yield put(actionStatusStarted({ actionName: CLEAR_USER_MESSAGE }));
        yield put(postUserSuccess(''));
        yield put(deleteUserSuccess(''));
        yield put(actionStatusSuccess({ actionName: CLEAR_USER_MESSAGE }));
    } catch (e) {
        actionStatusFailed({
            actionName: CLEAR_USER_MESSAGE,
            error: e,
        });
    }
}

export default [
    takeLatest(`${GET_ALL_USERS}`, getAllUsers),
    takeLatest(`${GET_SOLD_TO_SHIP_TO}`, getAllSoldToShipTo),
    takeLatest(`${GET_ROLES}`, getAllRoles),
    takeLatest(`${GET_CHANNELS}`, getAllChannels),
    takeLatest(`${GET_TIERS}`, getFilteredTiers),
    takeEvery(`${POST_USER}`, postUser),
    takeEvery(`${DELETE_USER}`, deleteUser),
    takeEvery(`${CLEAR_USER_MESSAGE}`, clearMessage),
];
